import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Icon,
  Image,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import NoOrder from '~/assets/images/order.png';

import Content from '~/containers/Content';
import PageContainer from '~/containers/PageContainer';

import BackwardBar from '~/components/BackwardBar';

import { ConcatModal, OrderCollapseContainer } from './components';
import OrderDetail from './OrderDetail';

import { IoCashOutline, IoRestaurantOutline } from 'react-icons/io5';

import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import authAtom from '~/recoil/atom/auth';
import platformAtom from '~/recoil/atom/platform';

import useOrders from '~/pages/OrderList/hooks/useOrders';

import * as fp from 'lodash/fp';

import transformDateRangeToString from '~/utils/transformDateRangeToString';

import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { AiOutlineMessage } from 'react-icons/ai';
import { BsPersonFill } from 'react-icons/bs';
import { WiTime3 } from 'react-icons/wi';
import globalEnterpriseGroupBuyAtom from '~/recoil/atom/enterpriseGroupBuy/globalEnterpriseGroupBuyAtom';
import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import { PaymentType } from '~/types/common';
import {
  EnterpriseGroupBuy,
  EnterpriseGroupBuyStatus,
  PaymentTypeString,
} from '~/types/enterpriseGroupBuy';
import getPath from '~/utils/getNavigatePath';

const EnterpriseGroupBuyOrderLeftBlock = ({ data }: { data: EnterpriseGroupBuy | null }) => {
  if (!data) return <></>;

  return (
    <Box px={4} flex={1}>
      <Content py={5}>
        <VStack
          m="auto"
          w="fit-content"
          bg="#FFF"
          borderRadius="5px"
          p="8px 16px"
          border="1px solid #E5B369"
          spacing="16px"
        >
          <Text fontSize="16px" fontWeight={700} color="#333">
            {data?.name || '-'}的團購
          </Text>
          <Text fontSize="16px" w="full" fontWeight={700} color="#333" textAlign="left">
            {data?.status === EnterpriseGroupBuyStatus.ONGOING
              ? '正在團購中'
              : data?.status === EnterpriseGroupBuyStatus.RECEIVED
              ? '餐點準備中'
              : ''}
          </Text>
          <VStack spacing="8px" minW="343px" divider={<Divider borderColor="#E5B369" />}>
            <HStack
              w="full"
              flex={1}
              align="center"
              justify="space-between"
              fontSize="14px"
              fontWeight={400}
              color="#333"
            >
              <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                {<Icon as={WiTime3} fontSize="20px" />}
                <Text>取餐時間</Text>
              </HStack>
              <Text fontWeight={700}>
                {data?.pickMealAt && data?.pickMealEnd
                  ? format(data?.pickMealAt.toDate(), 'M/d(eee) HH:mm', { locale: zhTW }) +
                    format(data?.pickMealEnd.toDate(), '-HH:mm', { locale: zhTW })
                  : '-'}
              </Text>
            </HStack>
            <HStack
              w="full"
              flex={1}
              align="center"
              justify="space-between"
              fontSize="14px"
              fontWeight={400}
              color="#333"
            >
              <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                {<Icon as={IoCashOutline} fontSize="20px" />}
                <Text>付款方式</Text>
              </HStack>
              <Text fontWeight={700}>
                {data?.paymentType ? PaymentTypeString?.[data?.paymentType] : '-'}
              </Text>
            </HStack>
          </VStack>
          <VStack w="full">
            <HStack
              w="full"
              flex={1}
              align="center"
              justify="space-between"
              fontSize="14px"
              fontWeight={400}
              color="#333"
            >
              <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                {<Icon as={BsPersonFill} fontSize="20px" />}
                <Text>主辦人</Text>
              </HStack>
              <Text fontWeight={700}>{data?.sponsorName || '-'}</Text>
            </HStack>
            <HStack
              w="full"
              flex={1}
              align="center"
              justify="space-between"
              fontSize="14px"
              fontWeight={400}
              color="#333"
            >
              <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                <Text />
              </HStack>
              <Text fontWeight={700}>{data?.sponsorPhone || '-'}</Text>
            </HStack>

            {data?.sponsorTalk ? (
              <>
                <Text
                  p="8px"
                  borderRadius="5px"
                  w="100%"
                  bg="#F9F9F9"
                  fontSize="14px"
                  fontWeight={400}
                >
                  <Icon as={AiOutlineMessage} fontSize="18px" /> {data?.sponsorTalk || ''}
                </Text>
              </>
            ) : null}
          </VStack>
        </VStack>
      </Content>
    </Box>
  );
};

export default function OrderList() {
  const auth = useRecoilValue(authAtom);
  const platform = useRecoilValue(platformAtom);
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const navigate = useNavigate();
  const { groupId = '', enterpriseGroupBuyId = '' } = useParams();
  const { data } = useRecoilValue(globalEnterpriseGroupBuyAtom);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [openOrderId, setOpenOrderId] = useState('');
  const [openPaymentDetailModal, setOpenPaymentDetailModal] = useState<PaymentType>(
    PaymentType.NONE,
  );

  const { orders, pastOrders } = useOrders(auth, platform?.id, enterpriseGroupBuyId);

  const handleOpenPaymentDetailModal = (paymentType: PaymentType) => {
    setOpenPaymentDetailModal(paymentType);
  };

  const handleClosePaymentDetailModal = () => {
    setOpenPaymentDetailModal(PaymentType.NONE);
  };

  useEffect(() => {
    openPaymentDetailModal !== PaymentType.NONE && handleClosePaymentDetailModal();
  }, [openOrderId]);

  const EnterpriseGroupBuyOrderList = () => {
    return (
      <VStack flex="1" align="stretch" spacing={4}>
        {orders.map((order) => {
          return (
            <OrderCollapseContainer
              key={order.id}
              isOpen={fp.eq(order.id, openOrderId)}
              orderNumber={order.orderNumber}
              completeTimeString={transformDateRangeToString(
                order.pickMealAt?.toDate() || null,
                order.pickMealEnd?.toDate() || null,
              )}
              onOpen={() => setOpenOrderId((prev) => (fp.eq(prev, order.id) ? '' : order.id))}
            >
              <Box py={4}>
                <OrderDetail
                  order={order}
                  onOpenConcatModal={onOpen}
                  onOpenPaymentDetailModal={handleOpenPaymentDetailModal}
                />
              </Box>
            </OrderCollapseContainer>
          );
        })}

        {pastOrders.map((order) => {
          return (
            <OrderCollapseContainer
              key={order.id}
              isOpen={fp.eq(order.id, openOrderId)}
              orderNumber={order.orderNumber}
              completeTimeString={transformDateRangeToString(
                order.pickMealAt?.toDate() || null,
                order.pickMealEnd?.toDate() || null,
              )}
              onOpen={() => setOpenOrderId((prev) => (fp.eq(prev, order.id) ? '' : order.id))}
            >
              <Box py={4}>
                <OrderDetail
                  order={order}
                  onOpenConcatModal={onOpen}
                  onOpenPaymentDetailModal={handleOpenPaymentDetailModal}
                />
              </Box>
            </OrderCollapseContainer>
          );
        })}
      </VStack>
    );
  };

  const DefaultOrderList = () => {
    return (
      <Stack direction={{ base: 'column', md: 'row' }} spacing={5}>
        <VStack minW="325px" flex="1" align="stretch" spacing={4}>
          <Text fontSize="sm" color="gray.500">
            進行中的訂單
          </Text>
          {orders.map((order) => {
            return (
              <OrderCollapseContainer
                key={order.id}
                isOpen={fp.eq(order.id, openOrderId)}
                orderNumber={order.orderNumber}
                completeTimeString={transformDateRangeToString(
                  order.pickMealAt?.toDate() || null,
                  order.pickMealEnd?.toDate() || null,
                )}
                onOpen={() => setOpenOrderId((prev) => (fp.eq(prev, order.id) ? '' : order.id))}
              >
                <Box py={4}>
                  <OrderDetail
                    order={order}
                    onOpenConcatModal={onOpen}
                    onOpenPaymentDetailModal={handleOpenPaymentDetailModal}
                  />
                </Box>
              </OrderCollapseContainer>
            );
          })}
        </VStack>

        <VStack minW="325px" flex="1" align="stretch" spacing={4}>
          <Text fontSize="sm" color="gray.500">
            過去訂單
          </Text>

          {pastOrders.map((order) => {
            return (
              <OrderCollapseContainer
                key={order.id}
                isOpen={fp.eq(order.id, openOrderId)}
                orderNumber={order.orderNumber}
                completeTimeString={transformDateRangeToString(
                  order.pickMealAt?.toDate() || null,
                  order.pickMealEnd?.toDate() || null,
                )}
                onOpen={() => setOpenOrderId((prev) => (fp.eq(prev, order.id) ? '' : order.id))}
              >
                <Box py={4}>
                  <OrderDetail
                    order={order}
                    onOpenConcatModal={onOpen}
                    onOpenPaymentDetailModal={handleOpenPaymentDetailModal}
                  />
                </Box>
              </OrderCollapseContainer>
            );
          })}
        </VStack>
      </Stack>
    );
  };

  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          {!!enterpriseGroupBuyId && data?.status === EnterpriseGroupBuyStatus.RECEIVED ? null : (
            <BackwardBar
              title="繼續點餐"
              onNavigate={() => navigate(getPath(`/`, groupId, enterpriseGroupBuyId))}
            />
          )}
          <Heading
            py={3}
            size="md"
            bgColor="white"
            textAlign="center"
            borderBottomWidth="1px"
            borderColor="primary"
          >
            訂單
          </Heading>
        </>
      }
    >
      <Stack
        flexDir={isLargerThan768 ? 'row' : 'column'}
        w={isLargerThan768 ? 'fit-content' : 'full'}
        align={isLargerThan768 ? 'flex-start' : 'center'}
        justify={isLargerThan768 ? 'center' : 'flex-start'}
        m="auto"
      >
        {!!enterpriseGroupBuyId ? <EnterpriseGroupBuyOrderLeftBlock data={data} /> : null}

        <Box px={4} flex={1}>
          <Content py={2}>
            {pastOrders.length === 0 && orders.length === 0 ? (
              <VStack py={10} spacing={6}>
                <Image src={NoOrder} w={180} />
                <Text fontSize="sm" color="secondary">
                  目前沒有訂單
                </Text>
                {!!enterpriseGroupBuyId &&
                data?.status === EnterpriseGroupBuyStatus.RECEIVED ? null : (
                  <Button
                    onClick={() => navigate(getPath('/', groupId, enterpriseGroupBuyId))}
                    px={12}
                    py={5}
                    size="sm"
                    bgColor="Secondary600"
                    color="white"
                    fontSize="md"
                    fontWeight="bold"
                    justifyContent="center"
                    alignItems="center"
                    _hover={{ bgColor: 'Secondary600', color: 'white' }}
                    _active={{ bgColor: 'Secondary600', color: 'white', opacity: 0.8 }}
                    leftIcon={<Icon as={IoRestaurantOutline} boxSize="1.125rem" />}
                  >
                    去點餐
                  </Button>
                )}
              </VStack>
            ) : (
              <>{!!enterpriseGroupBuyId ? <EnterpriseGroupBuyOrderList /> : <DefaultOrderList />}</>
            )}
          </Content>
        </Box>
      </Stack>
      <ConcatModal isOpen={isOpen} onClose={onClose} phoneNumber={platform.phoneNumber} />
      {/* <TzWalletModal
        isOpen={openPaymentDetailModal === PaymentType.TZ_WALLET}
        onClose={handleClosePaymentDetailModal}
      /> */}
    </PageContainer>
  );
}
